import { z } from 'zod';

// ─── Chart sub-schemas ────────────────────────────────────────────────────────

const pointSchema = z.object({ x: z.string(), y: z.number() });

const linePointSchema = z.object({ x: z.union([z.string(), z.number()]), y: z.number() });

const lineSeriesSchema = z.object({
  id: z.string(),
  label: z.string().optional(),
  data: z.array(linePointSchema),
});

const lineChartSchema = z.object({
  title: z.string(),
  xAxisTitle: z.string(),
  yAxisTitle: z.string(),
  id: z.string(),
  data: z.array(lineSeriesSchema),
  tooltipTemplate: z.string().optional(),
});

const multipleLineChartSchema = z.object({
  title: z.string(),
  xAxisTitle: z.string(),
  yAxisTitle: z.string(),
  data: z.array(lineSeriesSchema),
  tooltipTemplate: z.string().optional(),
});

const barDataSchema = z.object({
  index: z.string(),
  data: z.array(pointSchema),
});

const barChartSchema = z.object({
  title: z.string(),
  xAxisTitle: z.string(),
  yAxisTitle: z.string(),
  indexBy: z.string(),
  data: z.array(pointSchema),
  tooltipTemplate: z.string().optional(),
});

const multipleBarChartSchema = z.object({
  title: z.string(),
  xAxisTitle: z.string(),
  yAxisTitle: z.string(),
  indexBy: z.string(),
  data: z.array(barDataSchema),
  tooltipTemplate: z.string().optional(),
});

const pieDataPointSchema = z.object({
  id: z.string(),
  label: z.string(),
  value: z.number(),
});

const pieChartSchema = z.object({
  title: z.string(),
  xAxisTitle: z.string(),
  yAxisTitle: z.string(),
  data: z.array(pieDataPointSchema),
  tooltipTemplate: z.string().optional(),
});

const chartOptionsSchema = z.object({
  n: z.number().optional(),
  unit: z.string().optional(),
  isPrefix: z.boolean().optional(),
}).optional();

const multipleChartOptionsSchema = z.object({
  seriesToN: z.record(z.string(), z.number()).optional(),
  unit: z.string().optional(),
  isPrefix: z.boolean().optional(),
}).optional();

const dataTableSchema = z.object({
  records: z.array(z.array(z.string().nullable())),
  rowNames: z.array(z.array(z.string().nullable())),
  columnNames: z.array(z.array(z.string().nullable())),
  /**
   * Optional per-cell metadata (arrow indicators, significance flags).
   * Mirrors the `metadata` field of `QuantFileTableData`.
   */
  metadata: z.object({
    arrowColors: z.array(z.array(z.number())).optional(),
    arrowDirection: z.array(z.array(z.string())).optional(),
    isSignificant: z.array(z.array(z.boolean())).optional(),
  }).optional(),
});

const sortConfigSchema = z.object({
  columnIndex: z.number(),
  direction: z.enum(['asc', 'desc']),
}).nullable().optional();

// ─── ChartWidgetDataSchema ────────────────────────────────────────────────────

/**
 * Zod schema for ChartWidgetData — mirrors the QuickChartData discriminated union.
 * All 8 chart variants are validated individually so a bad shape is caught early.
 */
export const ChartWidgetDataSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('bar'), chart: barChartSchema, colors: z.array(z.string()).optional(), options: chartOptionsSchema, insights: z.string().optional() }),
  z.object({ type: z.literal('stacked-bar'), chart: barChartSchema, colors: z.array(z.string()).optional(), options: chartOptionsSchema, insights: z.string().optional() }),
  z.object({ type: z.literal('pie'), chart: pieChartSchema, colors: z.array(z.string()).optional(), options: chartOptionsSchema, insights: z.string().optional() }),
  z.object({ type: z.literal('multiple-clustered-bar'), chart: multipleBarChartSchema, colors: z.array(z.string()).optional(), options: multipleChartOptionsSchema, insights: z.string().optional() }),
  z.object({ type: z.literal('multiple-stacked-bar'), chart: multipleBarChartSchema, colors: z.array(z.string()).optional(), options: multipleChartOptionsSchema, insights: z.string().optional() }),
  z.object({ type: z.literal('line'), chart: lineChartSchema, colors: z.array(z.string()).optional(), options: chartOptionsSchema, insights: z.string().optional() }),
  z.object({ type: z.literal('multiple-line'), chart: multipleLineChartSchema, colors: z.array(z.string()).optional(), options: chartOptionsSchema, insights: z.string().optional() }),
  z.object({
    type: z.literal('data-table'),
    title: z.string(),
    headerColor: z.string(),
    data: dataTableSchema,
    customColumnNames: z.array(z.array(z.string())).optional(),
    customRowNames: z.array(z.array(z.string())).optional(),
    sortConfig: sortConfigSchema,
    insights: z.string().optional(),
  }),
  z.object({
    type: z.literal('kpi-widget'),
    title: z.string(),
    backgroundColor: z.string(),
    values: z.array(z.string()),
    labels: z.array(z.string()),
    insights: z.string().optional(),
  }),
  z.object({
    type: z.literal('progress-bar-widget'),
    title: z.string(),
    backgroundColor: z.string(),
    values: z.array(z.string()),
    labels: z.array(z.string()),
    colors: z.array(z.string()).optional(),
    minValues: z.array(z.string()).optional(),
    maxValues: z.array(z.string()).optional(),
    units: z.array(z.string()).optional(),
    insights: z.string().optional(),
  }),
  z.object({
    type: z.literal('semi-circle-progress-widget'),
    title: z.string(),
    backgroundColor: z.string(),
    values: z.array(z.string()),
    labels: z.array(z.string()),
    colors: z.array(z.string()).optional(),
    minValues: z.array(z.string()).optional(),
    maxValues: z.array(z.string()).optional(),
    units: z.array(z.string()).optional(),
    insights: z.string().optional(),
  }),
]);

// ─── Canonical per-widget data shapes ────────────────────────────────────────

/**
 * `ChartWidgetData` is derived from `ChartWidgetDataSchema`, making the Zod
 * schema the single source of truth for both TypeScript types and runtime
 * validation. `QuickChartData` in widget-grid.ts re-exports this type so all
 * existing consumers continue to work without modification.
 */
export type ChartWidgetData = z.infer<typeof ChartWidgetDataSchema>;

/**
 * Text widget data — a `content` string.
 * For markdown mode this is the raw markdown; for rich text a serialised representation.
 */
export const TextWidgetDataSchema = z.object({
  content: z.string(),
});
export interface TextWidgetData {
  content: string;
}

/** Metric card widget data — parallel arrays of display values and labels */
export const MetricCardWidgetDataSchema = z.object({
  values: z.array(z.string()),
  labels: z.array(z.string()),
});
export interface MetricCardWidgetData {
  values: string[];
  labels: string[];
}

/** Code widget data — arbitrary key/value pairs passed into the iframe sandbox */
export const CodeWidgetDataSchema = z.record(z.string(), z.unknown());
export type CodeWidgetData = Record<string, unknown>;

// ─── Rep Dashboard sub-schemas ─────────────────────────────────────────────

const RepDashboardKpiItemSchema = z.object({
  /** Display label for the KPI card (e.g. "Calls per Day") */
  label: z.string(),
  /** Main numeric value displayed large (e.g. "9.14") */
  value: z.string(),
  /** Unit shown next to the value (e.g. "HCPs", "%") */
  unit: z.string(),
  /** Optional secondary value shown below with separator (e.g. "74 TRx") */
  subValue: z.string().optional(),
  /** Change percentage string (e.g. "+5.09%", "-33.03%", "0%") */
  change: z.string(),
  /** Label describing the comparison period (e.g. "prior QTD", "prior 13 Weeks") */
  changeLabel: z.string(),
  /** Determines badge color: green for positive, red for negative, gray for neutral */
  changeType: z.enum(['positive', 'negative', 'neutral']),
});

const RepDashboardTableRowSchema = z.object({
  /** Unique identifier displayed in the ID column (e.g. "13133082") */
  id: z.string(),
  /** Full name of the HCP */
  name: z.string(),
  /** Location/address string */
  address: z.string(),
  /** Target number of calls for this cycle */
  callGoal: z.number(),
  /** Number of calls still needed to reach the goal */
  remainingCalls: z.number(),
  /** Optional HCP profile ID — renders an ArrowButton that sends a chat message to fetch HCP details */
  profileId: z.string().optional(),
});

const RepDashboardRelatedQuestionSchema = z.object({
  /** The question text displayed and submitted as a chat message when clicked */
  text: z.string(),
});

/** Rep Dashboard widget data — multi-section pharma sales rep performance dashboard */
export const RepDashboardWidgetDataSchema = z.object({
  /** Banner summary text shown at the top with lightbulb icon */
  summaryText: z.string(),
  /** KPI cards displayed in the collapsible performance section */
  kpis: z.array(RepDashboardKpiItemSchema),
  /** Bullet-point opportunity/insight strings shown in the collapsible opportunities section */
  opportunities: z.array(z.string()),
  /** Optional highlighted info box text shown below the opportunities list */
  infoBoxText: z.string().optional(),
  /** Informational text displayed above the HCP table (e.g. "Here are the HCPs in your call plan…") */
  tableInfo: z.string().optional(),
  /** HCP table rows displayed in the paginated table section */
  tableRows: z.array(RepDashboardTableRowSchema),
  /** Related questions shown at the bottom — clicking submits the text as a chat message */
  relatedQuestions: z.array(RepDashboardRelatedQuestionSchema).optional(),
});
export type RepDashboardKpiItem = z.infer<typeof RepDashboardKpiItemSchema>;
export type RepDashboardTableRow = z.infer<typeof RepDashboardTableRowSchema>;
export type RepDashboardRelatedQuestion = z.infer<typeof RepDashboardRelatedQuestionSchema>;
export type RepDashboardWidgetData = z.infer<typeof RepDashboardWidgetDataSchema>;

// ─── Quote Widget Data ───────────────────────────────────────────────────────

/** Quote widget data — a content string with optional speaker attribution */
export const QuoteWidgetDataSchema = z.object({
  content: z.string(),
  speaker: z.string().optional(),
});
export interface QuoteWidgetData {
  content: string;
  speaker?: string;
}

// ─── Data Source Metadata ────────────────────────────────────────────────────

/**
 * Optional metadata describing where the widget data originated.
 * Carried alongside canonical data so consumers can display provenance info.
 */
export const DataSourceMetadataSchema = z.object({
  /** Human-readable label for the data source (e.g. "Claims Data Q1-Q4 2024") */
  label: z.string().optional(),
  /** Source type identifier (e.g. "file", "api", "ai-generated") */
  type: z.string().optional(),
  /** Timestamp when the data was last fetched / generated */
  fetchedAt: z.string().optional(),
});
export type DataSourceMetadata = z.infer<typeof DataSourceMetadataSchema>;

// ─── CanonicalWidgetDataSchema ────────────────────────────────────────────────

/**
 * Runtime-validated discriminated union for all widget data shapes.
 *
 * Agents must return JSON matching one of these variants. The proxy endpoint
 * rejects responses that don't conform so that malformed data never reaches
 * the rendering layer.
 *
 * Agents should return extra fields where useful — they are stripped by
 * `safeParse`. But required fields per variant (e.g. `chart`, `content`,
 * `values`/`labels`) must be present or the response is rejected.
 */
export const CanonicalWidgetDataSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('chart'), data: ChartWidgetDataSchema, sourceMetadata: DataSourceMetadataSchema.optional() }),
  z.object({ type: z.literal('text'), data: TextWidgetDataSchema, sourceMetadata: DataSourceMetadataSchema.optional() }),
  z.object({ type: z.literal('metric_card'), data: MetricCardWidgetDataSchema, sourceMetadata: DataSourceMetadataSchema.optional() }),
  z.object({ type: z.literal('code'), data: CodeWidgetDataSchema, sourceMetadata: DataSourceMetadataSchema.optional() }),
  z.object({ type: z.literal('rep_dashboard'), data: RepDashboardWidgetDataSchema, sourceMetadata: DataSourceMetadataSchema.optional() }),
  z.object({ type: z.literal('quote'), data: QuoteWidgetDataSchema, sourceMetadata: DataSourceMetadataSchema.optional() }),
  z.object({ type: z.literal('segment_overview'), data: z.object({ narrative: z.string().optional(), commercialAction: z.string().optional(), gapAnalysis: z.union([z.string(), z.record(z.string(), z.unknown())]).optional() }), sourceMetadata: DataSourceMetadataSchema.optional() }),
]);

/**
 * CanonicalWidgetData is the data shape that the DashboardDataContext holds
 * for each widget. Widgets read this via `useDashboardData(widgetId)` and
 * transform it into the specific props expected by their Widget UI.
 */
export type CanonicalWidgetData =
  | { type: 'chart'; data: ChartWidgetData; sourceMetadata?: DataSourceMetadata }
  | { type: 'text'; data: TextWidgetData; sourceMetadata?: DataSourceMetadata }
  | { type: 'metric_card'; data: MetricCardWidgetData; sourceMetadata?: DataSourceMetadata }
  | { type: 'code'; data: CodeWidgetData; sourceMetadata?: DataSourceMetadata }
  | { type: 'rep_dashboard'; data: RepDashboardWidgetData; sourceMetadata?: DataSourceMetadata }
  | { type: 'quote'; data: QuoteWidgetData; sourceMetadata?: DataSourceMetadata }
  | { type: 'segment_overview'; data: { narrative?: string; commercialAction?: string; gapAnalysis?: string | Record<string, unknown> }; sourceMetadata?: DataSourceMetadata };
